import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Platform,
  TextInput,
  Pressable,
} from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import Checkbox from 'expo-checkbox';
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import { ChargerAttributes } from '../models/charger-model';
import { notificationService } from '../services/notification-service';
import { UserPlanAssociation } from '../models/user-plan-associations-model';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { applyDriverPlanFees } from '../components/common/utils/get-price-info';
import {
  calculatePeak,
  generatePeakObj,
} from '../components/common/utils/peak-off-peak-field';
import { getScheduleConflicts } from '../components/common/utils/unavailable-times-utils';

// Helper to format a Date object into a local datetime string "YYYY-MM-DDTHH:mm"
const formatDateToLocalDatetime = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatWithoutSeconds = (date: Date): string => {
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

type ExtraOption = 'none' | 'duration' | 'maxKwh';

interface SchedulerProps {
  selectedCharger: ChargerAttributes;
  scheduleDate: Date;
  setScheduleTimes: (
    startTime: Date,
    endTime: Date | null,
    maxKwh?: number | null
  ) => void;
  clearSchedule: () => void;
  presetDate?: Date;
}

const Scheduler: React.FC<SchedulerProps> = ({
  selectedCharger,
  scheduleDate,
  setScheduleTimes,
  clearSchedule,
  presetDate,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(
    presetDate ? presetDate : scheduleDate
  );
  const [confirmedDate, setConfirmedDate] = useState<Date | null>(
    presetDate ?? null
  );
  const [durationHours, setDurationHours] = useState(8);
  const [durationMinutes, setDurationMinutes] = useState(0);
  const [maxKwh, setMaxKwh] = useState(0);
  const [showDuration, setShowDuration] = useState(false);
  const [showMaxKwh, setShowMaxKwh] = useState(false);
  const [estCost, setEstCost] = useState<number>(0);
  const [offPeakChecked, setOffPeakChecked] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    startTime?: string;
    endTime?: string;
    maxKwh?: string;
  }>({});
  const driverPlan: UserPlanAssociation | null = useSelector(
    (state: RootState) => state.user.driverPlan
  );

  const openModal = () => {
    setValidationErrors({}); // Clear any previous validation errors
    setModalVisible(true);
  };
  const closeModal = () => {
    // Cancel = "no schedule, charge now". Reset the picker's own date too so
    // reopening the modal doesn't resurrect a previously spun/confirmed time.
    setConfirmedDate(null);
    setSelectedDate(new Date());
    setOffPeakChecked(false);
    clearSchedule();
    setValidationErrors({}); // Clear validation errors when closing
    setModalVisible(false);
  };

  const offPeakCharging = () => {
    // find the next off-peak charge time
    const now = new Date();
    const peakData = generatePeakObj(selectedCharger.peak_selection);

    // map JS getDay() → day name
    const dayNames = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const today = dayNames[now.getDay()];

    // pull today's window (or fallback to no‑peak)
    const { start, end } = peakData[today] ?? { start: 0, end: 0 };

    // if start===end → no peak window → treat as always off-peak
    if (start !== end) {
      // build Date objects for today's start & end times
      const startTime = new Date(now);
      startTime.setHours(start, 0, 0, 0);

      const endTime = new Date(now);
      endTime.setHours(end, 0, 0, 0);

      // if now is between [start, end)
      if (now >= startTime && now < endTime) {
        console.log(endTime); // e.g. "Wed Apr 23 2025 19:00:00 GMT-0600 (MDT)"
        setSelectedDate(endTime);
        return;
      }
    }

    setSelectedDate(now);
  };

  const handleCheckboxToggle = (newValue: boolean) => {
    setOffPeakChecked(newValue);
    if (newValue) {
      offPeakCharging();
    } else {
      const now = new Date();
      setSelectedDate(now);
    }
  };

  // For web: Parse the input value and create a local Date object.
  const handleWebDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value; // Format: "YYYY-MM-DDTHH:mm"
    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    const localDate = new Date(year, month - 1, day, hour, minute);
    const now = new Date();

    // Clear previous validation errors for start time
    setValidationErrors(prev => ({ ...prev, startTime: undefined }));

    // Check if the selected date is more than 24 hours away from now
    if (Math.abs(localDate.getTime() - now.getTime()) > 24 * 60 * 60 * 1000) {
      setValidationErrors(prev => ({
        ...prev,
        startTime: 'Selected date must be within 24 hours of the current time.'
      }));
      notificationService.sendNotification(
        'error',
        'Selected date must be within 24 hours of the current time.'
      );
      // Still update the date for user to see what they selected
      setSelectedDate(localDate);
      return;
    }
    
    if (localDate < now) {
      setValidationErrors(prev => ({
        ...prev,
        startTime: 'Cannot set a time in the past!'
      }));
      notificationService.sendNotification(
        'error',
        'Cannot set a time in the past!'
      );
      // Still update the date for user to see what they selected
      setSelectedDate(localDate);
    } else {
      setSelectedDate(localDate);
    }
  };

  // Conditionally render the date picker based on platform.
  // Render the date picker. On web, use the HTML input with local time formatting.
  const renderDatePicker = () => {
    const picker =
      Platform.OS === 'web' ? (
        <input
          style={{
            fontFamily: 'Poppins',
            fontSize: 16,
            padding: 10,
            width: '100%',
            borderRadius: 4,
            border: '1px solid #ccc',
          }}
          type="datetime-local"
          value={formatDateToLocalDatetime(selectedDate)}
          onChange={handleWebDateChange}
        />
      ) : (
        // <DatePicker
        //   date={selectedDate}
        //   onDateChange={setSelectedDate}
        //   mode="datetime"
        // />
        <DateTimePicker
          value={selectedDate}
          onChange={(_, date) => {
            if (date) {
              const now = new Date();
              
              // Clear previous validation errors for start time
              setValidationErrors(prev => ({ ...prev, startTime: undefined }));
              
              // Check if the selected date is more than 24 hours away from now
              if (Math.abs(date.getTime() - now.getTime()) > 24 * 60 * 60 * 1000) {
                setValidationErrors(prev => ({
                  ...prev,
                  startTime: 'Selected date must be within 24 hours of the current time.'
                }));
                // Still update the date for user to see what they selected
                setSelectedDate(date);
                return;
              }
              
              if (date < now) {
                setValidationErrors(prev => ({
                  ...prev,
                  startTime: 'Cannot set a time in the past!'
                }));
                // Still update the date for user to see what they selected
                setSelectedDate(date);
              } else {
                setSelectedDate(date);
              }
            }
          }}
          mode="datetime"
          display="spinner" // iOS-only spinner style
          textColor="#FFFFFF" // white text on spinner
          themeVariant="dark" // forces the dark wheel style on iOS 14+
        />
      );

    // wrap picker + checkbox inline
    return (
      <View
        style={{
          flexDirection: Platform.OS === 'web' ? 'row' : 'column',
          alignItems: 'center',
          marginTop: 10,
          marginBottom: 10,
        }}
      >
        {picker}

        {Platform.OS === 'web' ? (
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: 20,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={offPeakChecked}
              onChange={(e) => handleCheckboxToggle(e.target.checked)}
            />
            <span style={{ marginLeft: 6, color: '#fff' }}>Off‑Peak only</span>
          </label>
        ) : (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginLeft: 20,
            }}
          >
            <Checkbox
              value={offPeakChecked}
              onValueChange={handleCheckboxToggle}
            />
            <Text style={{ marginLeft: 6, fontSize: 16, color: '#fff' }}>
              Off‑Peak only
            </Text>
          </View>
        )}
      </View>
    );
  };

  const handleConfirm = () => {
    const now = new Date();
    const startTime = selectedDate;
    let hasErrors = false;
    const newErrors: typeof validationErrors = {};

    // Validate start time
    if (startTime < now) {
      newErrors.startTime = 'Cannot set a time in the past!';
      hasErrors = true;
    } else if (Math.abs(startTime.getTime() - now.getTime()) > 24 * 60 * 60 * 1000) {
      newErrors.startTime = 'Selected date must be within 24 hours of the current time.';
      hasErrors = true;
    }

    // Calculate end time for validation if duration is set
    let endTime: Date | null = null;
    let duration = 0;
    if (showDuration) {
      duration = durationHours + durationMinutes / 60;
      endTime = new Date(startTime.getTime() + duration * 60 * 60 * 1000);
      
      // Validate that end time is after start time
      if (endTime <= startTime) {
        newErrors.endTime = 'End time must be after start time!';
        hasErrors = true;
      }
    }

    // Validate max kWh if set
    let scheduleMaxKwh: number | null = showMaxKwh
      ? maxKwh > 0
        ? maxKwh
        : null
      : null;
    
    if (showMaxKwh && (maxKwh <= 0 || isNaN(maxKwh))) {
      newErrors.maxKwh = 'Max kWh must be a positive number!';
      hasErrors = true;
    }

    // Check for unavailable-times conflicts
    if (!hasErrors && endTime) {
      const conflicts = getScheduleConflicts(
        selectedCharger.unavailable_times ?? null,
        startTime,
        endTime
      );
      if (conflicts) {
        const days = conflicts.map((c) => c.day).join(', ');
        newErrors.startTime = `Schedule conflicts with unavailable hours on ${days}`;
        hasErrors = true;
      }
    }

    // Update validation errors
    setValidationErrors(newErrors);

    // If there are errors, don't close the modal and show notification
    if (hasErrors) {
      const errorMessages = Object.values(newErrors).filter(Boolean);
      notificationService.sendNotification(
        'error',
        errorMessages.join(' ')
      );
      return; // Keep modal open
    }

    // If validation passes, proceed with setting the schedule
    setConfirmedDate(selectedDate);
    console.log('Selected Date/Time:', selectedDate);
    setScheduleTimes(startTime, endTime, scheduleMaxKwh);
    setModalVisible(false);
  };

  // Display the confirmed date/time on the button.
  const getButtonText = () => {
    return confirmedDate ? formatWithoutSeconds(confirmedDate) : 'Set Schedule';
  };

  useEffect(() => {
    const d = new Date();
    setSelectedDate(presetDate ? presetDate : d);
  }, []);

  useEffect(() => {
    const estCostPerKwhPeak = applyDriverPlanFees(
      selectedCharger.financials.price_kwh_peak,
      driverPlan
    );
    const estCostPerKwhOffPeak = applyDriverPlanFees(
      selectedCharger.financials.price_kwh_off_peak,
      driverPlan
    );
    let finalCost: number;
    let finalDurationCost = 0;
    let finalMaxKwhCost = 0;
    if (showDuration) {
      const endDate = new Date(
        selectedDate.getTime() + (durationHours * 60 + durationMinutes) * 60000
      );
      // get peak/offpeak times
      const { peakTime, offPeakTime } = calculatePeak(
        selectedDate.toISOString(),
        endDate.toISOString(),
        selectedCharger.peak_selection
      );
      const power = ((selectedCharger.charging_capacity ?? 32) * 220) / 1000;
      //calculate the estimated peak/offpeak costs for the entire time period
      finalDurationCost =
        (peakTime / (60 * 60)) * estCostPerKwhPeak * power +
        (offPeakTime / (60 * 60)) * estCostPerKwhOffPeak * power;
    } else if (showMaxKwh) {
      //estimate number of hours to get to max kWh (A * V )
      const numHrs =
        maxKwh / (((selectedCharger.charging_capacity ?? 40) * 220) / 1000); //assume 40 watts if not specified, 220V
      const endDate = new Date(selectedDate.getTime() + numHrs * 60 * 60000);

      // get peak/offpeak times
      const { peakTime, offPeakTime } = calculatePeak(
        selectedDate.toISOString(),
        endDate.toISOString(),
        selectedCharger.peak_selection
      );

      //calculate the estimated peak/offpeak costs to get to the specified max kWh
      finalMaxKwhCost =
        (peakTime / (60 * 60)) * estCostPerKwhPeak +
        (offPeakTime / (60 * 60)) * estCostPerKwhOffPeak;
    }

    finalCost =
      finalDurationCost > finalMaxKwhCost ? finalDurationCost : finalMaxKwhCost;
    setEstCost(finalCost);
  }, [durationHours, durationMinutes, maxKwh, showDuration, showMaxKwh]);

  return (
    <View style={styles.outerContainer}>
      <TouchableOpacity style={styles.button} onPress={openModal}>
        <View style={styles.buttonContent}>
          <MaterialIcons
            name="schedule"
            size={20}
            color="#fff"
            style={styles.icon}
          />
          <Text style={styles.buttonText}>{getButtonText()}</Text>
        </View>
      </TouchableOpacity>

      <Modal visible={modalVisible} transparent animationType="fade">
        <View style={styles.modalBackground}>
          {/* Prevent closing when tapping inside the modal */}
          <View style={styles.modalContainer}>
            {/* Close button in the top right-hand corner */}
            <AntDesign
              onPress={() => setModalVisible(false)}
              name="closecircleo"
              size={16}
              color="#fff"
              style={styles.closeButton}
            />
            <Text style={styles.title}>Select Date &amp; Time</Text>
            {renderDatePicker()}

            {/* Validation Error Messages */}
            {validationErrors.startTime && (
              <Text style={styles.errorText}>
                {validationErrors.startTime}
              </Text>
            )}

            {/* Option selectors (both optional) */}
            <View style={styles.optionSelector}>
              <TouchableOpacity
                style={[
                  styles.optionButton,
                  showDuration && styles.optionButtonSelected,
                ]}
                onPress={() => setShowDuration((prev) => !prev)}
              >
                <Text
                  style={[
                    styles.optionButtonText,
                    showDuration && styles.optionButtonTextSelected,
                  ]}
                >
                  Set Duration (optional)
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.optionButton,
                  showMaxKwh && styles.optionButtonSelected,
                ]}
                onPress={() => setShowMaxKwh((prev) => !prev)}
              >
                <Text
                  style={[
                    styles.optionButtonText,
                    showMaxKwh && styles.optionButtonTextSelected,
                  ]}
                >
                  Set Max kWh (optional)
                </Text>
              </TouchableOpacity>
            </View>

            {/* Conditionally render extra inputs */}
            {showDuration && (
              <View style={styles.durationContainer}>
                <View style={styles.durationPicker}>
                  <Text style={styles.label}>Hours</Text>
                  <View style={styles.controls}>
                    <TouchableOpacity
                      onPress={() =>
                        setDurationHours((prev) => (prev > 0 ? prev - 1 : 0))
                      }
                    >
                      <Text style={styles.controlButton}>-</Text>
                    </TouchableOpacity>
                    <TextInput
                      style={styles.valueInput}
                      value={String(durationHours)}
                      onChangeText={(text) => {
                        const parsed = parseInt(text, 10);
                        if (!isNaN(parsed)) {
                          if (parsed < 0) {
                            setDurationHours(0);
                          } else if (parsed > 12) {
                            notificationService.sendNotification(
                              'error',
                              'Hours cannot be greater than 12'
                            );
                            setDurationHours(12);
                          } else {
                            setDurationHours(parsed);
                          }
                        } else {
                          setDurationHours(0);
                        }
                      }}
                      keyboardType="numeric"
                    />
                    <TouchableOpacity
                      onPress={() =>
                        setDurationHours((prev) => (prev < 12 ? prev + 1 : 12))
                      }
                    >
                      <Text style={styles.controlButton}>+</Text>
                    </TouchableOpacity>
                  </View>
                </View>
                <View style={styles.durationPicker}>
                  <Text style={styles.label}>Minutes</Text>
                  <View style={styles.controls}>
                    <TouchableOpacity
                      onPress={() =>
                        setDurationMinutes((prev) => (prev > 0 ? prev - 1 : 0))
                      }
                    >
                      <Text style={styles.controlButton}>-</Text>
                    </TouchableOpacity>
                    <TextInput
                      style={styles.valueInput}
                      value={String(durationMinutes)}
                      onChangeText={(text) => {
                        const parsed = parseInt(text, 10);
                        if (!isNaN(parsed)) {
                          if (parsed < 0) {
                            setDurationMinutes(0);
                          } else if (parsed > 59) {
                            notificationService.sendNotification(
                              'error',
                              'Minutes cannot be greater than 59'
                            );
                            setDurationMinutes(59);
                          } else {
                            setDurationMinutes(parsed);
                          }
                        } else {
                          setDurationMinutes(0);
                        }
                      }}
                      keyboardType="numeric"
                    />
                    <TouchableOpacity
                      onPress={() =>
                        setDurationMinutes((prev) =>
                          prev < 59 ? prev + 1 : 59
                        )
                      }
                    >
                      <Text style={styles.controlButton}>+</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            )}
            {/* Duration validation error */}
            {showDuration && validationErrors.endTime && (
              <Text style={styles.errorText}>
                {validationErrors.endTime}
              </Text>
            )}

            {showMaxKwh && (
              <View style={styles.maxKwhContainer}>
                <Text style={styles.label}>Max kWh</Text>
                <View style={styles.maxKwhInputContainer}>
                  <TextInput
                    style={styles.valueInput}
                    value={String(maxKwh)}
                    onChangeText={(text) => {
                      const parsed = parseInt(text, 10);
                      if (!isNaN(parsed) && parsed > 100) {
                        notificationService.sendNotification(
                          'error',
                          'Value cannot exceed 100kWh'
                        );
                        return;
                      }
                      setMaxKwh(isNaN(parsed) ? 0 : parsed);
                    }}
                    keyboardType="numeric"
                  />
                  <Text style={styles.kwhLabel}>kWh</Text>
                </View>
              </View>
            )}
            {/* Max kWh validation error */}
            {showMaxKwh && validationErrors.maxKwh && (
              <Text style={styles.errorText}>
                {validationErrors.maxKwh}
              </Text>
            )}

            {/* Show estimated charge only if at least one extra option is selected */}
            {(showDuration || showMaxKwh) && (
              <Text
                style={styles.estimatedCharge}
              >{`Max Estimated Cost: $${estCost.toFixed(2)}`}</Text>
            )}

            <View style={styles.buttonRow}>
              <TouchableOpacity
                style={[
                  styles.confirmButton,
                  { flexDirection: 'row', alignItems: 'center' },
                ]}
                onPress={handleConfirm}
              >
                <MaterialIcons
                  name="schedule"
                  size={20}
                  color="#fff"
                  style={styles.icon}
                />
                <Text style={styles.confirmButtonText}>Schedule</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={closeModal}
              >
                <Text style={styles.cancelButtonText}>
                  {presetDate && `Charge Now`}
                  {!presetDate && `Cancel`}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default Scheduler;

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    alignItems: 'center',
    // This container can be adjusted to match your app's layout.
    paddingHorizontal: 0,
    marginBottom: 0,
  },
  button: {
    backgroundColor: '#39BEB780',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 8,
    minHeight: 56,
    justifyContent: 'center',
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressableOverlay: {
    ...StyleSheet.absoluteFillObject, // Makes the overlay cover the entire screen
  },
  optionSelector: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginVertical: 15,
  },
  optionButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#39BEB7',
    borderRadius: 45,
    marginHorizontal: 5,
  },
  optionButtonSelected: {
    backgroundColor: '#39BEB780',
  },
  optionButtonText: {
    color: '#FFF',
    fontFamily: 'Poppins',
    textAlign: 'center',
  },
  optionButtonTextSelected: {
    color: '#fff',
  },
  icon: {
    marginRight: 8,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: 'PlusJakartaSans-Regular',
    fontWeight: '500',
  },
  modalBackground: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  modalContainer: {
    backgroundColor: '#0B1E1D',
    width: '90%',
    maxWidth: 350,
    padding: 20,
    borderRadius: 10,
    alignItems: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  durationContainer: {
    flexDirection: 'row',
    marginVertical: 20,
  },
  durationPicker: {
    alignItems: 'center',
    marginHorizontal: 15,
  },
  label: {
    marginBottom: 5,
    fontSize: 16,
    color: '#fff',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  controlButton: {
    fontSize: 24,
    width: 32,
    textAlign: 'center',
    backgroundColor: '#eee',
    borderRadius: 4,
    marginHorizontal: 5,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  valueInput: {
    fontSize: 18,
    color: '#fff',
    textAlign: 'center',
    width: 30,
    padding: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#fff',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  value: {
    fontSize: 18,
    minWidth: 30,
    textAlign: 'center',
    color: '#fff',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  maxKwhContainer: {
    alignItems: 'center',
    marginVertical: 20,
  },
  maxKwhInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  kwhLabel: {
    marginLeft: 5,
    fontSize: 16,
    color: '#fff',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  estimatedCharge: {
    fontSize: 12,
    color: '#3FC67D',
    marginTop: 4,
    fontFamily: 'Poppins',
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: 20,
  },
  confirmButton: {
    //flex: 1,
    backgroundColor: '#3FC67D',
    paddingVertical: 10,
    paddingHorizontal: 25,
    borderRadius: 8,
    marginRight: 10,
  },
  confirmButtonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: 'PlusJakartaSans-Regular',
  },
  cancelButton: {
    //flex: 1,
    backgroundColor: 'transparent',
    paddingVertical: 10,
    paddingHorizontal: 25,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#39BEB780',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#39BEB780',
    fontFamily: 'PlusJakartaSans-Regular',
  },
  closeButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    padding: 5,
  },
  closeText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#fff',
  },
  errorText: {
    color: '#ff4444',
    fontSize: 14,
    textAlign: 'center',
    marginVertical: 5,
    marginHorizontal: 10,
    fontFamily: 'PlusJakartaSans-Regular',
  },
});
